function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
/**
 * Copyright (c) 2022 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
// just import type. forge is imported dynamically,

import * as Sentry from '@sentry/browser';

// Node-forge mentions that strictMode=false can resolve some ASN.1 parse errors
// Ref. https://github.com/digitalbazaar/forge for node-forge sample code of handling pkcs12
const asn1ParseStrictMode = false;
export class P12Service {
  constructor(forgeLib) {
    this.forgeLib = forgeLib;
    /**
     * @return undefined if password is invalid
     * */
    _defineProperty(this, "attemptDecodeP12Ans1WithPassword", (p12Asn1, password) => {
      try {
        const p12 = this.forgeLib.pkcs12.pkcs12FromAsn1(p12Asn1, asn1ParseStrictMode, password);
        return {
          isValidPassword: true,
          p12
        };
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log('Failed to decode P12 with password', error);
        Sentry.captureException(error);
        // It seems that there is no way to definitely verify password correctness.
        // The node-forge lib just decrypts using the password and exceptions are considered as invalid password.
        const knownErrorMessages = [
        // Unable to decrypt PKCS#8 ShroudedKeyBag, wrong password
        'wrong password',
        // PKCS#12 MAC could not be verified. Invalid password
        'Invalid password', 'Too few bytes to parse DER', 'Only 8, 16, 24, or 32 bits supported', 'Too few bytes to read ASN.1 value'];
        if (error instanceof Error) {
          const err = error;
          if (knownErrorMessages.some(msg => err.message.includes(msg))) {
            return {
              isValidPassword: false
            };
          }
        }
        throw error;
      }
    });
    _defineProperty(this, "getBags", (p12, bagName, bagType) => {
      const searchResult = p12.getBags({
        bagType
      });
      const bags = searchResult[bagType];
      if (!bags || !bags.length) {
        throw new Error(`Invalid p12 format. ${bagName} is not found`);
      }
      return bags;
    });
    _defineProperty(this, "binaryStringToArrayBuffer", s => {
      const array = new Uint8Array(s.length);
      for (let i = 0; i < s.length; i++) {
        array[i] = s.charCodeAt(i);
      }
      return array;
    });
    _defineProperty(this, "decryptP12File", async (file, password) => {
      const arrayBuffer = await file.arrayBuffer();
      // DER is binary format of certificate
      const p12Der = this.forgeLib.util.createBuffer(arrayBuffer);
      // ASN: is abstract syntax notation format of certificate
      const p12Asn1 = this.forgeLib.asn1.fromDer(p12Der, asn1ParseStrictMode);
      // Parse content from ASN format
      return this.attemptDecodeP12Ans1WithPassword(p12Asn1, password);
    });
    _defineProperty(this, "generateP12NoPassword", p12 => {
      const certBagType = this.forgeLib.pki.oids.certBag;
      const privateKeyBagType = this.forgeLib.pki.oids.pkcs8ShroudedKeyBag;
      const keyBags = this.getBags(p12, 'Private key', privateKeyBagType);
      if (keyBags.length > 1) {
        throw new Error('Invalid p12 file. There is more than one private key');
      }
      const key = keyBags[0].key;
      if (!key) {
        throw new Error('Invalid p12 file. Key bag does not have content');
      }
      const certBags = this.getBags(p12, 'Certificate (public key)', certBagType);
      const certs = certBags.map(bag => {
        if (!bag.cert) {
          throw new Error('Invalid p12 file. Cert bag does not have content');
        }
        return bag.cert;
      });
      const p12Asn1Result = this.forgeLib.pkcs12.toPkcs12Asn1(key, certs[0], '', {
        algorithm: '3des'
      });
      const p12Der = this.forgeLib.asn1.toDer(p12Asn1Result);
      return this.binaryStringToArrayBuffer(p12Der.getBytes());
    });
  }
}
export const createP12Service = async () => {
  const forgeLib = await import('node-forge');
  return new P12Service(forgeLib);
};